import { Factory } from "@/data/dao/Factory";
import type { IDataSource } from "@/data/dao/service/BaseServiceDao";
import { DocumentDao } from "@/data/dao/service/DocumentDao";
import type { IDocumentCondition } from "@/data/model/Document";
import { Weekday } from "@/data/model/Enums";
import type { IRoom } from "@/data/model/Room";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { DocumentConditionWindow } from "@/portal/modal/DocumentConditionWindow";
import { PortalFormat } from "@/portal/util/PortalFormat";

export class DocumentConditionEditor {
    private $element: JQuery;
    private dao?: DocumentDao;
    private documentId?: string;

    private dataTable?: DataTable<IDocumentCondition>;
    private search?: TableFilter;
    private roomDataSource?: IDataSource<IRoom>;

    constructor(element: JQuery) {
        this.$element = element;
    }

    public loadForDocument(
        documentId: string,
        roomDataSource: IDataSource<IRoom>,
        abortSignal?: AbortSignal
    ): DocumentConditionEditor {
        this.roomDataSource = roomDataSource;

        if (this.dao === undefined || this.dataTable === undefined) {
            this.roomDataSource.getData({}, abortSignal).then((rooms) => {
                this.dao = Factory.getInstance<DocumentDao>(DocumentDao);

                this.documentId = documentId;

                const tableConfig: IDataTableConfig<IDocumentCondition> = {
                    columnMapping: [
                        {
                            name: i18n("general.entities.room"),
                            property: "roomId",
                            styles: { width: "22%" },
                            formatter: (m, p) => {
                                if (p === undefined) return i18n("documents.allRooms");

                                const room = rooms.items.find((c) => c.id === p);
                                if (room === undefined) return i18n("documents.unknownRoom");

                                return $('<a href="/room/' + room.id + '">' + room.name + "</a>").click((e) => {
                                    e.stopPropagation();
                                });
                            },
                        },
                        {
                            name: i18n("documents.fields.dateFrom"),
                            property: "dateFrom",
                            styles: { width: "22%" },
                            formatter: (m) => PortalFormat.timeRange(m.dateFrom, m.dateUntil),
                        },
                        {
                            name: i18n("documents.fields.weekdays"),
                            property: "weekdays",
                            styles: { width: "22%" },
                            formatter: (m, p) => {
                                let str = "";
                                for (const i in p) str += i18n("enums.ShortWeekDay." + Weekday[p[i]]) + ", ";

                                if (str.length > 2) str = str.substr(0, str.length - 2);

                                return str;
                            },
                        },
                        {
                            name: i18n("documents.fields.timeFrom"),
                            property: "timeFrom",
                            styles: { width: "22%" },
                            formatter: (m) => PortalFormat.timeSpanRange(m.timeFrom, m.timeUntil),
                        },
                        {
                            name: "",
                            property: "id",
                            styles: { width: "110px", "text-align": "right" },
                            formatter: () => {
                                return $(
                                    '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                                );
                            },
                        },
                    ],
                    autoload: false,
                    useJsHistory: false,
                    filterProvider: {
                        getFilter: () => this.search!.getValue(),
                        setFilter: (d) => this.search!.setValue(d),
                    },
                };
                this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));

                this.dataTable = new DataTable<IDocumentCondition>(
                    this.$element.find("> .kt-portlet__body > .kt-datatable"),
                    this.dao.getConditionDataSource(this.documentId),
                    tableConfig
                );
                this.dataTable.onClick((m) => {
                    this.onEditCondition(m);
                });

                this.search.addAction(
                    i18n("documents.newCondition"),
                    () => {
                        this.onEditCondition();
                    },
                    "la la-plus"
                );

                this.dataTable.listenTo(this.search);
                this.dataTable.loadData();
            });
        }

        return this;
    }

    private onEditCondition(model?: IDocumentCondition) {
        const modal = new DocumentConditionWindow(this.documentId!, model, this.roomDataSource);
        modal.onSaved = () => this.dataTable!.loadData();
        modal.onRemoved = () => this.dataTable!.loadData();

        modal.show();
    }
}
